/**
 * PasswordStep Component
 * Step 3: Create a password for the verified email.
 * Registers with Cognito (PreSignUp trigger auto-confirms) and signs in.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Lock, AlertCircle, Check, X, Loader2 } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { validatePassword } from '@/lib/password';

interface PasswordStepProps {
  password: string;
  confirmPassword: string;
  error: string | null;
  isLoading: boolean;
  onPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (confirmPassword: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  canProceed: boolean;
}

export const PasswordStep: React.FC<PasswordStepProps> = ({
  password,
  confirmPassword,
  error,
  isLoading,
  onPasswordChange,
  onConfirmPasswordChange,
  onBack,
  onSubmit,
  canProceed,
}) => {
  const { t } = useTranslation();
  const passwordValidation = useMemo(() => validatePassword(password), [password]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canProceed) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {t('signup.createPassword')}
        </h2>
        <p className="text-slate-600">
          {t('signup.chooseSecurePassword')}
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
            {t('signup.passwordLabel')}
          </label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPasswordChange(e.target.value)}
            leftIcon={<Lock className="w-5 h-5" />}
            variant="emerald"
            inputSize="lg"
            placeholder={t('signup.createPasswordPlaceholder')}
            required
            autoComplete="new-password"
            autoFocus
          />
          {password && (
            <ul className="mt-2 space-y-1">
              {passwordValidation.checks.map((check) => (
                <li key={check.label} className="flex items-center gap-1.5 text-xs">
                  {check.met ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  )}
                  <span className={check.met ? 'text-emerald-600' : 'text-slate-500'}>
                    {check.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
            {t('signup.confirmPasswordLabel')}
          </label>
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onConfirmPasswordChange(e.target.value)}
            leftIcon={<Lock className="w-5 h-5" />}
            variant="emerald"
            inputSize="lg"
            placeholder={t('signup.confirmPasswordPlaceholder')}
            required
            autoComplete="new-password"
            error={password && confirmPassword && password !== confirmPassword ? t('signup.passwordsDoNotMatch') : undefined}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={onBack}
          variant="ghost"
          size="lg"
          shape="rounded"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>

        <Button
          type="submit"
          disabled={!canProceed || isLoading}
          variant="gradient-emerald"
          size="lg"
          shape="rounded"
          className="flex-1"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('signup.creatingAccount')}
            </span>
          ) : (
            t('signup.createAccountButton')
          )}
        </Button>
      </div>
    </form>
  );
};

export default PasswordStep;
